/**
 * useUpdateSecret Hook
 *
 * Custom React Query mutation hook for updating secrets.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { secretService } from '../../../api/secret.service';
import type { SecretUpdateDTO } from '../../../types/secret.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UseUpdateSecretOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * Hook for updating secret
 * @param tenantId - Tenant ID
 * @param secretId - Secret ID
 * @param options - Success/error callbacks
 */
export const useUpdateSecret = (
  tenantId: string,
  secretId: string,
  options?: UseUpdateSecretOptions
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SecretUpdateDTO) => secretService.updateSecret(tenantId, secretId, data),
    onSuccess: () => {
      // Invalidate both list and details queries
      queryClient.invalidateQueries({ queryKey: ['secrets', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['secrets', tenantId, secretId] });
      options?.onSuccess?.();
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error);
      options?.onError?.(errorMessage);
    },
  });
};
